/**
 * EscapedVisits — #251 review fix (should-fix 4).
 *
 * The check-out money leg is client-orchestrated (check-out POST, then
 * open-invoice POST): a crash/tab-close between the two leaves a
 * checked-out booking with NO money record — invisible to the
 * open-invoices queue AND to the Payment ledger. This card surfaces the
 * server's reconciliation read (GET /api/v2/admin/payments/
 * reconciliation/escaped-visits: checked_out bookings with neither a
 * completed Payment nor any OpenInvoice row) and lets staff RECOVER each
 * one by queueing its invoice — which drops it into the normal
 * open-invoices settle flow.
 *
 * Renders nothing when the list is empty (the healthy steady state) so
 * the Payments page carries no dead chrome.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  adminPaymentsApi,
  type AdminEscapedVisit,
} from '../../lib/api.js';

type EscapedVisitsProps = {
  readonly apiUrl: string;
  /** Parent bumps this to force a refetch (shared with the queue). */
  readonly refreshKey?: number;
  /** Called after a visit is successfully re-queued as an open invoice. */
  readonly onRecorded?: () => void;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; visits: AdminEscapedVisit[] };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function EscapedVisits({
  apiUrl,
  refreshKey = 0,
  onRecorded,
}: EscapedVisitsProps): JSX.Element | null {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const res = await adminPaymentsApi.getEscapedVisits(apiUrl);
    if (res.error || !res.data) {
      setState({
        kind: 'error',
        message: res.error ?? 'Could not check for unrecorded visits',
      });
      return;
    }
    setState({ kind: 'ready', visits: res.data.visits ?? [] });
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleRecord = useCallback(
    async (visit: AdminEscapedVisit) => {
      setRecordingId(visit.bookingId);
      setRecordError(null);
      // The server derives customer/service from the booking and the
      // queue endpoint is per-booking idempotent (#251 should-fix 3), so
      // a retry here can never mint a duplicate invoice.
      const res = await adminPaymentsApi.createOpenInvoice(apiUrl, {
        bookingId: visit.bookingId,
        totalCents: visit.totalCents,
        notes: 'Recovered from escaped-visits reconciliation',
      });
      setRecordingId(null);
      if (res.error || !res.data?.invoice) {
        setRecordError(
          `Could not record the invoice for booking ${visit.bookingId.slice(0, 8)}: ${res.error ?? 'unknown error'}`,
        );
        return;
      }
      await load();
      onRecorded?.();
    },
    [apiUrl, load, onRecorded],
  );

  // Healthy steady state — no chrome. Errors DO render (an invisible
  // reconciliation read failure would defeat the surface's purpose).
  if (state.kind === 'ready' && state.visits.length === 0 && !recordError) {
    return null;
  }
  if (state.kind === 'loading') return null;

  return (
    <article
      className="ferro-card"
      aria-label="Unrecorded visits"
      data-testid="escaped-visits"
    >
      <header className="ferro-card__header">
        <h2 className="ferro-section__subtitle">
          Unrecorded visits — money funnel gaps
        </h2>
        {state.kind === 'ready' ? (
          <span className="ferro-badge ferro-badge--warning">
            {state.visits.length}
          </span>
        ) : null}
      </header>

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {recordError ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="escaped-visits-record-error"
        >
          <p>{recordError}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setRecordError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' && state.visits.length > 0 ? (
        <>
          <p className="ferro-muted">
            These bookings were checked out but never entered the money
            funnel — no invoice and no payment exists for them. Record each
            one to put it back in the open-invoices queue.
          </p>
          <ul className="ferro-table" aria-label="Unrecorded visit list">
            {state.visits.map((v) => (
              <li
                key={v.bookingId}
                className="ferro-table__row"
                data-testid={`escaped-visit-${v.bookingId}`}
              >
                <div className="ferro-table__body">
                  <div className="ferro-table__primary">
                    {v.customerName || `Customer ${v.customerId.slice(0, 8)}`}
                    {' · '}
                    {formatMoney(v.totalCents)}
                  </div>
                  <div className="ferro-table__meta">
                    {v.serviceTypeName || 'Service'} ·{' '}
                    {formatDate(v.serviceDate)} · booking #
                    {v.bookingId.slice(0, 8)}
                  </div>
                </div>
                <div className="ferro-table__actions">
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    disabled={recordingId === v.bookingId}
                    onClick={() => void handleRecord(v)}
                    data-testid={`escaped-visit-record-${v.bookingId}`}
                  >
                    {recordingId === v.bookingId
                      ? 'Recording…'
                      : 'Record invoice'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </article>
  );
}
